import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shirt, Leaf, Snowflake, Briefcase, Pill, Recycle,
  Sofa, Users, MapPin, Code
} from 'lucide-react';

interface CategoryCard3DProps {
  title: string;
  slug: string;
  productCount: number;
  color: string;
}

const CategoryCard3D: React.FC<CategoryCard3DProps> = ({ 
  title, 
  slug, 
  productCount,
  color
}) => {
  const [transform, setTransform] = useState({ rotateX: 0, rotateY: 0 });
  const cardRef = useRef<HTMLDivElement>(null);

  const getIcon = (slug: string) => {
    switch (slug) {
      case 'garments-apparel':
        return <Shirt className="w-8 h-8" />;
      case 'jute-natural-fiber':
        return <Leaf className="w-8 h-8" />;
      case 'frozen-foods':
        return <Snowflake className="w-8 h-8" />;
      case 'leather-goods':
        return <Briefcase className="w-8 h-8" />;
      case 'pharmaceuticals-health':
        return <Pill className="w-8 h-8" />;
      case 'scrap-recycling':
        return <Recycle className="w-8 h-8" />;
      case 'furniture':
        return <Sofa className="w-8 h-8" />;
      case 'manpower':
        return <Users className="w-8 h-8" />;
      case 'tourism':
        return <MapPin className="w-8 h-8" />;
      case 'freelancer':
        return <Code className="w-8 h-8" />;
      default:
        return <Briefcase className="w-8 h-8" />;
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;

    const rotateX = (mouseY / rect.height) * -20;
    const rotateY = (mouseX / rect.width) * 20;

    setTransform({ rotateX, rotateY });
  };

  const handleMouseLeave = () => {
    setTransform({ rotateX: 0, rotateY: 0 });
  };

  return (
    <Link to={`/category/${slug}`} className="block w-full">
      <div
        ref={cardRef}
        className="relative w-full h-24 sm:h-28 md:h-32 perspective-1000 group cursor-pointer"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ perspective: '1000px' }}
      >
        <div
          className="relative w-full h-full transition-transform duration-300 ease-out transform-style-preserve-3d touch-manipulation"
          style={{
            transform: `rotateX(${transform.rotateX}deg) rotateY(${transform.rotateY}deg)`,
            transformStyle: 'preserve-3d'
          }}
        >
          {/* Main Card */}
          <div 
            className="absolute inset-0 rounded-xl sm:rounded-2xl shadow-lg overflow-hidden transform-style-preserve-3d"
            style={{
              background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)',
              transformStyle: 'preserve-3d'
            }}
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-2 sm:top-4 right-2 sm:right-4 w-8 sm:w-12 md:w-16 h-8 sm:h-12 md:h-16 rounded-full bg-white/20"></div>
              <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 w-6 sm:w-8 md:w-12 h-6 sm:h-8 md:h-12 rounded-full bg-white/15"></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 sm:w-16 md:w-20 h-12 sm:h-16 md:h-20 rounded-full bg-white/10"></div>
            </div>

            {/* Content Layer */}
            <div 
              className="relative z-10 p-2 sm:p-3 md:p-4 h-full flex flex-col justify-between text-white"
              style={{
                transform: 'translateZ(20px)',
                transformStyle: 'preserve-3d'
              }}
            >
              {/* Icon */}
              <div 
                className="flex justify-center mb-1 sm:mb-2 transform transition-transform duration-300 group-hover:scale-110"
                style={{
                  transform: 'translateZ(30px)',
                  transformStyle: 'preserve-3d'
                }}
              >
                <div className="text-white">
                  {React.cloneElement(getIcon(slug) as React.ReactElement, { 
                    className: "w-6 sm:w-8 md:w-10 lg:w-12 h-6 sm:h-8 md:h-10 lg:h-12" 
                  })}
                </div>
              </div>

              {/* Text Content */}
              <div 
                className="text-center"
                style={{
                  transform: 'translateZ(25px)',
                  transformStyle: 'preserve-3d'
                }}
              >
                <h3 className="font-bold text-xs sm:text-sm md:text-base lg:text-lg mb-1 leading-tight text-white line-clamp-2">
                  {title}
                </h3>
                <p className="text-xs sm:text-sm opacity-90 text-white">
                  {productCount} Products
                </p>
              </div>
            </div>

            {/* Floating Elements */}
            <div 
              className="absolute top-1 sm:top-2 right-1 sm:right-2 w-2 sm:w-3 h-2 sm:h-3 bg-white/30 rounded-full transform transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
              style={{
                transform: 'translateZ(40px)',
                transformStyle: 'preserve-3d'
              }}
            ></div>
            <div 
              className="absolute bottom-1 sm:bottom-2 left-1 sm:left-2 w-1.5 sm:w-2 h-1.5 sm:h-2 bg-white/25 rounded-full transform transition-transform duration-700 group-hover:-translate-x-1 group-hover:translate-y-1"
              style={{
                transform: 'translateZ(35px)',
                transformStyle: 'preserve-3d'
              }}
            ></div>

            {/* Shine Effect */}
          </div>

          {/* Shadow */}
          <div 
            className="absolute inset-0 bg-black/20 rounded-xl sm:rounded-2xl blur-xl transform transition-all duration-300 group-hover:blur-2xl group-hover:scale-105"
            style={{
              transform: 'translateZ(-10px) translateY(10px)',
              transformStyle: 'preserve-3d'
            }}
          ></div>
        </div>
      </div>
    </Link>
  );
};

export default CategoryCard3D;